import HistoryIcon from "@mui/icons-material/History";
import UpdateIcon from "@mui/icons-material/Update";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { styled } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { skipToken } from "@reduxjs/toolkit/query";
import { Link } from "react-router";
import { useSession } from "users/__session/session-context";
import { useGetWorkAgreementByIdQuery } from "work/work-agreements/_api/work-agreement-api";
import WorkAgreement from "work/work-agreements/work-agreement";
import Proposal from "work/proposals/proposal";

/**
 * Where the proposal's work came from and went to, which is the first thing anybody opening a
 * revision or a hired proposal wants to know -- so it opens the tab rather than trailing the dates.
 * It spans every column so the fields below keep the two-column arrangement they have without it.
 */
const HistoryField = styled("div")({
  display: "flex",
  flexDirection: "column",
  gridColumn: "1 / -1",
});
const HistoryFieldTitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: "1.2rem",
}));

type Props = Readonly<{
  proposal: Proposal;
}>;

/**
 * The proposal's own ends of the links between a proposal and the work agreements it touches, in a
 * History field at the top of the Details tab. The draft, active (which also serves the revise
 * route) and archived pages all draw it, so the three cannot drift.
 *
 * - **The work this revises** (#6630): a change order names the hired agreement it was opened
 *   against, from its first draft. It is the mirror of `WorkAgreementRevisionLinks`' "Open the
 *   revision request".
 * - **The work this produced** (#5692): a hired proposal names the agreement it was hired into. It is
 *   the mirror of `WorkAgreementRevisionLinks`' "Open the hired proposal".
 *
 * A hired revision draws both: the previous revision and the one that replaced it.
 *
 * Each label and route follows the agreement's current state rather than being fixed when the link
 * was made: work that is still running opens the active view, and work that has ended, been
 * archived, or that the reader left (#6629) opens the archived view.
 *
 * The two links answer "may this reader open it" differently, because the server tells them
 * differently. The produced agreement's id reaches only a reader who may open it, so the id being
 * here at all is the permission to show it. The revised agreement's id reaches every reader of the
 * proposal -- a reviewer carried forward onto a change order may have no access to the work -- so
 * the link asks the agreement itself, and draws only once the server answers it. A refusal, an
 * absence, or a request still in flight draws nothing, because a link that opens onto a refusal is
 * worse than no link.
 *
 * Renders nothing, title included, for a proposal with neither link to draw.
 */
export default function WorkAgreementLinks({ proposal }: Props) {
  const session = useSession();
  const { replacesWorkAgreementId } = proposal;
  const revisedWorkQuery = useGetWorkAgreementByIdQuery(
    replacesWorkAgreementId ? { workAgreementId: replacesWorkAgreementId.value, session } : skipToken,
  );
  // `currentData`, not `data`: `data` keeps the last answer when the argument changes, skipToken
  // included, so a tab handed another proposal would link to the first one's work. The id check
  // holds the answer to the agreement this proposal names.
  const revisedWork =
    replacesWorkAgreementId && revisedWorkQuery.currentData?.id === replacesWorkAgreementId.value
      ? revisedWorkQuery.currentData
      : undefined;
  const producedWorkAgreementId = proposal.isHired ? proposal.workAgreementId : undefined;
  if (!revisedWork && !producedWorkAgreementId) return null;

  return (
    <HistoryField>
      <HistoryFieldTitle>History</HistoryFieldTitle>
      <Stack spacing={1.5}>
        {revisedWork && (
          <RevisedWorkLink isHired={proposal.isHired} workAgreement={WorkAgreement.fromJSON(revisedWork)} />
        )}
        {producedWorkAgreementId && (
          <WorkLink
            caption="This proposal was hired into this work."
            isActiveWork={proposal.workAgreement.isActiveWork}
            label={proposal.workAgreement.isActiveWork ? "Open the active work" : "Open the archived work"}
            workAgreementId={producedWorkAgreementId.value}
          />
        )}
      </Stack>
    </HistoryField>
  );
}

function RevisedWorkLink({ isHired, workAgreement }: Readonly<{ isHired: boolean; workAgreement: WorkAgreement }>) {
  if (!workAgreement.id) return null;
  const isActiveWork = workAgreement.isActiveWork && !workAgreement.isFrozenForReader;

  return (
    <WorkLink
      caption={
        isActiveWork
          ? "This proposal was opened to revise this work, which is still running."
          : "This proposal was opened to revise this work, which has ended."
      }
      isActiveWork={isActiveWork}
      label={isHired ? "Open the work this revised" : "Open the work this revises"}
      workAgreementId={workAgreement.id.value}
    />
  );
}

function WorkLink({
  caption,
  isActiveWork,
  label,
  workAgreementId,
}: Readonly<{ caption: string; isActiveWork: boolean; label: string; workAgreementId: string }>) {
  return (
    <Stack spacing={0.5}>
      <Button
        component={Link}
        size="small"
        startIcon={isActiveWork ? <UpdateIcon /> : <HistoryIcon />}
        sx={{ alignSelf: "flex-start" }}
        to={`/work/${isActiveWork ? "active" : "archived"}/view/${workAgreementId}/details`}
      >
        {label}
      </Button>
      <Typography color="text.secondary" variant="body2">
        {caption}
      </Typography>
    </Stack>
  );
}
